import { useCurrentUser } from '@artic/api-client';
import { Badge } from '@artic/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@artic/ui/collapsible';
import { useFeatureFlag } from '@artic/ui/feature-flag';
import { Logo } from '@artic/ui/logo';
import { LogoMark } from '@artic/ui/logo-mark';
import { Popover, PopoverContent, PopoverTrigger } from '@artic/ui/popover';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  useSidebar,
} from '@artic/ui/sidebar';
import {
  ArrowReloadHorizontalIcon,
  Book01Icon,
  Calendar03Icon,
  ChartColumnStackedIcon,
  CheckListIcon,
  ComputerTerminalIcon,
  CustomizeIcon,
  DashboardSquare01Icon,
  DocumentValidationIcon,
  DrawingModeIcon,
  FileImportIcon,
  Flag01Icon,
  GridIcon,
  Home01Icon,
  InboxIcon,
  LayersIcon,
  LiveStreaming01Icon,
  Notebook01Icon,
  Package01Icon,
  Settings05Icon,
  Shield01Icon,
  StructureCheckIcon,
  TaskDaily01Icon,
  TestTube01Icon,
  UserGroupIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { Link, useRouterState } from '@tanstack/react-router';
import * as React from 'react';

import { usePartnerName } from '../features/inbox/use-partner-name';
import { NavUser } from './nav-user';

type IconType = React.ComponentProps<typeof HugeiconsIcon>['icon'];

interface NavItem {
  title: string;
  icon?: IconType;
  /** Internal quality route */
  to?: string;
  /** External hub URL path (relative to hub base) */
  hubPath?: string;
  /** Fully-qualified external URL (rendered as-is, e.g. drawings.artic.works) */
  externalUrl?: string;
  hide?: boolean;
  disabled?: boolean;
  /** When true, only the exact path matches as active. */
  exact?: boolean;
  /**
   * Feature flag key. When set, the item routes through `FeatureFlagNavLink`:
   * the link is shown actively when the flag is on, faded when the flag is
   * off (linking to `offLink` if provided, otherwise disabled).
   */
  flag?: string;
  /** Route used when `flag` is off and a fallback target is desired. */
  offLink?: string;
}

interface NavSubheader {
  kind: 'subheader';
  label: string;
  items: NavItem[];
  hide?: boolean;
}

interface NavGroup {
  title: string;
  icon: IconType;
  items: (NavItem | NavSubheader)[];
  hide?: boolean;
  /** Open the group on first paint even when no child route is active. */
  defaultExpanded?: boolean;
}

function isSubheader(entry: NavItem | NavSubheader): entry is NavSubheader {
  return 'kind' in entry && entry.kind === 'subheader';
}

function getHubUrl(): string {
  const host = window.location.hostname;
  if (host.includes('staging')) return 'https://hub.staging.artic.works';
  if (host.includes('localhost')) return 'http://localhost:4000';
  return 'https://hub.artic.works';
}

function getDrawingControlUrl(): string {
  const host = window.location.hostname;
  if (host.includes('staging')) return 'https://drawings.staging.artic.works';
  if (host.includes('localhost')) return 'http://localhost:5173';
  return 'https://drawings.artic.works';
}

function useNavItems(): NavGroup[] {
  const { user } = useCurrentUser();
  const isAdmin = user.email.endsWith('@artic.works') || user.email.endsWith('@trunk.works');
  const planner = useFeatureFlag('planner');
  const isTrunkUser = isAdmin || user.email.endsWith('@redfiredigital.uk');
  const qualityInbox = useFeatureFlag('quality_inbox');
  const changeControl = useFeatureFlag('change_control');
  const drawingControlUrl = React.useMemo(() => getDrawingControlUrl(), []);

  return React.useMemo(() => {
    const groups: NavGroup[] = [
      {
        title: 'Factory',
        icon: DashboardSquare01Icon,
        defaultExpanded: true,
        items: [
          { title: 'Statusboard', icon: DashboardSquare01Icon, hubPath: '/statusboard' },
          { title: 'Flow', icon: StructureCheckIcon, hubPath: '/schedules/progress' },
          { title: 'Job Cards', icon: TaskDaily01Icon, hubPath: '/job-cards' },
        ],
      },
      {
        title: 'Quality',
        icon: CheckListIcon,
        defaultExpanded: true,
        items: [
          { title: 'Live Feed', icon: LiveStreaming01Icon, hubPath: '/feed' },
          { title: 'Inbox', icon: InboxIcon, to: '/inbox', hide: !qualityInbox },
          {
            title: 'Packs',
            icon: CheckListIcon,
            to: '/quality-packs',
            flag: 'quality_packs',
            // Flag off (non-admins): fall back to the legacy Hub Packs page.
            hubPath: '/quality/manufacture',
          },
          { title: 'Packs', icon: CheckListIcon, hubPath: '/quality/manufacture' },
          {
            title: 'Vision Test Plans',
            icon: TestTube01Icon,
            to: '/vision_test_plans',
            hide: !isTrunkUser,
          },
        ],
      },
      {
        title: 'Planner',
        icon: Calendar03Icon,
        defaultExpanded: true,
        items: [
          { title: 'Projects', icon: Calendar03Icon, to: '/projects', hide: !planner },
          { title: 'Production', icon: GridIcon, to: '/production-planner', hide: !planner },
          {
            title: 'Change Control',
            icon: ArrowReloadHorizontalIcon,
            to: '/change_control',
            // `change_control` defaults to admin_only, so the flag resolves true
            // only for Artic admins until a partner is promoted to released.
            hide: !changeControl,
          },
          { title: 'Workforce', icon: UserGroupIcon, hubPath: '/workforce' },
          {
            title: 'Drawing Control',
            icon: DrawingModeIcon,
            externalUrl: drawingControlUrl,
            hide: !isAdmin,
          },
        ],
      },
      {
        title: 'Catalog',
        icon: Book01Icon,
        defaultExpanded: true,
        items: [
          { title: 'Products', icon: LayersIcon, to: '/products', flag: 'planner' },
          {
            title: 'Operations',
            icon: LayersIcon,
            to: '/operations',
            hide: !planner,
          },
          {
            title: 'Bill of Operations',
            icon: Notebook01Icon,
            to: '/bill_of_operations',
            exact: true,
            flag: 'planner',
          },
        ],
      },
      {
        title: 'Settings',
        icon: Settings05Icon,
        items: [
          {
            kind: 'subheader',
            label: 'Factory',
            items: [
              {
                title: 'Factory Admin',
                icon: Home01Icon,
                to: '/factories',
                flag: 'settings_factory',
              },
              {
                title: 'Cause Categories',
                icon: CheckListIcon,
                to: '/cause_categories',
                flag: 'causes',
              },
            ],
          },
          {
            kind: 'subheader',
            label: 'Columns',
            items: [
              {
                title: 'DfMA Containers',
                icon: Package01Icon,
                to: '/products/containers',
                flag: 'planner',
              },
              {
                title: 'Columns',
                icon: ChartColumnStackedIcon,
                to: '/columns/structured-configuration',
                flag: 'planner',
              },
              {
                title: 'Custom Columns',
                icon: CustomizeIcon,
                to: '/columns/custom-configuration',
                flag: 'planner',
              },
            ],
          },
          {
            kind: 'subheader',
            label: 'Imports',
            items: [
              {
                title: 'Imports',
                icon: FileImportIcon,
                to: '/imports',
                flag: 'imports',
              },
            ],
          },
          {
            kind: 'subheader',
            label: 'Workforce',
            items: [
              { title: 'Shift Pattern', icon: Calendar03Icon, disabled: true },
              { title: 'Devices', icon: ComputerTerminalIcon, disabled: true },
            ],
          },
        ],
      },
      {
        title: 'Artic Admin',
        icon: Shield01Icon,
        hide: !isAdmin,
        items: [
          // Always on for admins (ENG-628) — the partner-facing counterpart
          // lives under Settings > Columns behind the planner flag.
          {
            title: 'Container Library',
            icon: Package01Icon,
            to: '/admin/containers',
          },
          {
            title: 'Audit Viewer',
            icon: DocumentValidationIcon,
            to: '/audit',
            exact: true,
          },
          { title: 'Feature Flags', icon: Flag01Icon, to: '/admin/feature-flags' },
          { title: 'Partners', icon: UserGroupIcon, to: '/admin/partners' },
        ],
      },
    ];

    return groups;
  }, [isAdmin, isTrunkUser, planner, qualityInbox, changeControl, drawingControlUrl]);
}

function visibleEntries(items: (NavItem | NavSubheader)[]): (NavItem | NavSubheader)[] {
  const result: (NavItem | NavSubheader)[] = [];
  for (const entry of items) {
    if (entry.hide) continue;
    if (!isSubheader(entry)) {
      result.push(entry);
      continue;
    }
    const visibleSubItems = entry.items.filter((i) => !i.hide);
    if (visibleSubItems.length === 0) continue;
    result.push({ ...entry, items: visibleSubItems });
  }
  return result;
}

function SmartLink({
  to,
  children,
  className,
}: {
  to: string;
  children: React.ReactNode;
  className?: string;
}) {
  if (to.startsWith('http') || to.startsWith('//')) {
    return (
      <a href={to} className={className}>
        {children}
      </a>
    );
  }
  return (
    <Link to={to} className={className}>
      {children}
    </Link>
  );
}

function resolveFlagLink(hubUrl: string, candidate: string | undefined): string | undefined {
  if (!candidate) return undefined;
  if (candidate.startsWith('http') || candidate.startsWith('/')) return candidate;
  return `${hubUrl}${candidate}`;
}

function NavLink({
  item,
  hubUrl,
  currentPath,
  isSub,
}: {
  item: NavItem;
  hubUrl: string;
  currentPath: string;
  isSub?: boolean;
}) {
  const subProps = isSub !== undefined ? { isSub } : {};
  if (item.flag) {
    return <FlaggedNavLink item={item} hubUrl={hubUrl} currentPath={currentPath} {...subProps} />;
  }
  return <PlainNavLink item={item} hubUrl={hubUrl} currentPath={currentPath} {...subProps} />;
}

function FlaggedNavLink({
  item,
  hubUrl,
  currentPath,
  isSub,
}: {
  item: NavItem;
  hubUrl: string;
  currentPath: string;
  isSub?: boolean;
}) {
  // Non-null asserted: NavLink only delegates here when item.flag is set.
  const enabled = useFeatureFlag(item.flag!);
  const subProps = isSub !== undefined ? { isSub } : {};

  if (enabled) {
    return <PlainNavLink item={item} hubUrl={hubUrl} currentPath={currentPath} {...subProps} />;
  }

  // Flag off: prefer a Hub fallback (hubPath / externalUrl) so the link still
  // reaches a real page. `resolveFlagLink` can't turn a leading-slash path into
  // a Hub URL (it would render an in-app <Link> to a dead route), so build the
  // absolute Hub href the same way PlainNavLink does.
  if (item.hubPath ?? item.externalUrl) {
    const href = item.externalUrl ?? `${hubUrl}${item.hubPath ?? ''}`;
    return <FadedNavLink item={item} href={href} {...subProps} />;
  }

  if (item.offLink) {
    const offLink = resolveFlagLink(hubUrl, item.offLink) ?? '';
    return <FadedNavLink item={item} href={offLink} {...subProps} />;
  }

  // Flag off, no fallback: render through the same disabled path as item.disabled.
  return (
    <PlainNavLink
      item={{ ...item, disabled: true }}
      hubUrl={hubUrl}
      currentPath={currentPath}
      {...subProps}
    />
  );
}

function FadedNavLink({ item, href, isSub }: { item: NavItem; href: string; isSub?: boolean }) {
  const inner = (
    <SmartLink to={href}>
      {item.icon && <HugeiconsIcon icon={item.icon} strokeWidth={2} />}
      <span>{item.title}</span>
    </SmartLink>
  );
  if (isSub) {
    return (
      <SidebarMenuSubItem>
        <SidebarMenuSubButton asChild className="opacity-60">
          {inner}
        </SidebarMenuSubButton>
      </SidebarMenuSubItem>
    );
  }
  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild tooltip={item.title} className="opacity-60">
        {inner}
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

function PlainNavLink({
  item,
  hubUrl,
  currentPath,
  isSub,
}: {
  item: NavItem;
  hubUrl: string;
  currentPath: string;
  isSub?: boolean;
}) {
  if (item.disabled) {
    if (isSub) {
      return (
        <SidebarMenuSubItem>
          <SidebarMenuSubButton asChild aria-disabled className="cursor-not-allowed opacity-50">
            <span>
              {item.icon && <HugeiconsIcon icon={item.icon} strokeWidth={2} />}
              <span>{item.title}</span>
            </span>
          </SidebarMenuSubButton>
        </SidebarMenuSubItem>
      );
    }
    return (
      <SidebarMenuItem>
        <SidebarMenuButton disabled aria-disabled tooltip={item.title}>
          {item.icon && <HugeiconsIcon icon={item.icon} strokeWidth={2} />}
          <span>{item.title}</span>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }

  const isActive = item.to
    ? item.exact
      ? currentPath === item.to
      : currentPath === item.to || currentPath.startsWith(`${item.to}/`)
    : false;

  if (item.to) {
    if (isSub) {
      return (
        <SidebarMenuSubItem>
          <SidebarMenuSubButton asChild isActive={isActive}>
            <Link to={item.to}>
              {item.icon && <HugeiconsIcon icon={item.icon} strokeWidth={2} />}
              <span>{item.title}</span>
            </Link>
          </SidebarMenuSubButton>
        </SidebarMenuSubItem>
      );
    }
    return (
      <SidebarMenuItem>
        <SidebarMenuButton asChild tooltip={item.title} isActive={isActive}>
          <Link to={item.to}>
            {item.icon && <HugeiconsIcon icon={item.icon} strokeWidth={2} />}
            <span>{item.title}</span>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }

  const href = item.externalUrl ?? `${hubUrl}${item.hubPath ?? ''}`;
  if (isSub) {
    return (
      <SidebarMenuSubItem>
        <SidebarMenuSubButton asChild>
          <a href={href}>
            {item.icon && <HugeiconsIcon icon={item.icon} strokeWidth={2} />}
            <span>{item.title}</span>
          </a>
        </SidebarMenuSubButton>
      </SidebarMenuSubItem>
    );
  }
  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild tooltip={item.title}>
        <a href={href}>
          {item.icon && <HugeiconsIcon icon={item.icon} strokeWidth={2} />}
          <span>{item.title}</span>
        </a>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

function ExpandedSubheaderRow({ label }: { label: string }) {
  return (
    <li className="text-sidebar-foreground/60 px-2 pt-2 pb-0.5 text-[10px] font-medium tracking-wider uppercase">
      {label}
    </li>
  );
}

function DisabledPopoverRow({ item }: { item: NavItem }) {
  return (
    <li
      aria-disabled
      className="text-sidebar-foreground/70 flex cursor-not-allowed items-center gap-2 rounded-md px-2 py-1.5 text-sm opacity-50"
      title="Not available for your account"
    >
      {item.icon && <HugeiconsIcon icon={item.icon} strokeWidth={2} className="size-4" />}
      <span>{item.title}</span>
    </li>
  );
}

function FlagPopoverItem({
  item,
  hubUrl,
  currentPath,
}: {
  item: NavItem;
  hubUrl: string;
  currentPath: string;
}) {
  // Hook must always run; non-null assertion is safe because the caller only
  // delegates to this component when `item.flag` is set.
  const enabled = useFeatureFlag(item.flag!);
  if (enabled) {
    return <PopoverItemContent item={item} hubUrl={hubUrl} currentPath={currentPath} />;
  }

  const offLink = item.offLink ? resolveFlagLink(hubUrl, item.offLink) : undefined;
  if (offLink) {
    const baseClass =
      'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground opacity-60';
    const inner = (
      <>
        {item.icon && <HugeiconsIcon icon={item.icon} strokeWidth={2} className="size-4" />}
        <span>{item.title}</span>
      </>
    );
    return (
      <li>
        {offLink.startsWith('http') ? (
          <a href={offLink} className={baseClass}>
            {inner}
          </a>
        ) : (
          <Link to={offLink} className={baseClass}>
            {inner}
          </Link>
        )}
      </li>
    );
  }

  return <DisabledPopoverRow item={item} />;
}

function PopoverItem({
  item,
  hubUrl,
  currentPath,
}: {
  item: NavItem;
  hubUrl: string;
  currentPath: string;
}) {
  if (item.flag) {
    return <FlagPopoverItem item={item} hubUrl={hubUrl} currentPath={currentPath} />;
  }
  return <PopoverItemContent item={item} hubUrl={hubUrl} currentPath={currentPath} />;
}

function PopoverItemContent({
  item,
  hubUrl,
  currentPath,
}: {
  item: NavItem;
  hubUrl: string;
  currentPath: string;
}) {
  if (item.disabled) {
    return <DisabledPopoverRow item={item} />;
  }

  const isActive = item.to
    ? item.exact
      ? currentPath === item.to
      : currentPath === item.to || currentPath.startsWith(`${item.to}/`)
    : false;

  const baseClass =
    'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground';
  const activeClass = isActive
    ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium'
    : '';

  if (item.to) {
    return (
      <li>
        <Link to={item.to} className={`${baseClass} ${activeClass}`.trim()}>
          {item.icon && <HugeiconsIcon icon={item.icon} strokeWidth={2} className="size-4" />}
          <span>{item.title}</span>
        </Link>
      </li>
    );
  }
  const href = item.externalUrl ?? `${hubUrl}${item.hubPath ?? ''}`;
  return (
    <li>
      <a href={href} className={baseClass}>
        {item.icon && <HugeiconsIcon icon={item.icon} strokeWidth={2} className="size-4" />}
        <span>{item.title}</span>
      </a>
    </li>
  );
}

function PopoverSubheaderRow({ label }: { label: string }) {
  return (
    <li className="text-sidebar-foreground/60 px-2 pt-2 pb-0.5 text-[10px] font-medium tracking-wider uppercase">
      {label}
    </li>
  );
}

function NavGroupRender({
  group,
  hubUrl,
  currentPath,
}: {
  group: NavGroup;
  hubUrl: string;
  currentPath: string;
}) {
  const { state } = useSidebar();
  const visible = visibleEntries(group.items);
  const [popoverOpen, setPopoverOpen] = React.useState(false);
  const closeTimerRef = React.useRef<number | null>(null);

  React.useEffect(
    () => () => {
      if (closeTimerRef.current !== null) window.clearTimeout(closeTimerRef.current);
    },
    [],
  );

  if (visible.length === 0) return null;

  if (state === 'collapsed') {
    const cancelClose = () => {
      if (closeTimerRef.current !== null) {
        window.clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    };
    const scheduleClose = () => {
      cancelClose();
      closeTimerRef.current = window.setTimeout(() => setPopoverOpen(false), 150);
    };
    const openNow = () => {
      cancelClose();
      setPopoverOpen(true);
    };
    return (
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <SidebarMenuItem onMouseEnter={openNow} onMouseLeave={scheduleClose}>
          <PopoverTrigger asChild>
            <SidebarMenuButton aria-haspopup="menu" aria-expanded={popoverOpen}>
              <HugeiconsIcon icon={group.icon} strokeWidth={2} />
              <span>{group.title}</span>
            </SidebarMenuButton>
          </PopoverTrigger>
        </SidebarMenuItem>
        <PopoverContent
          side="right"
          align="start"
          sideOffset={12}
          className="w-60 p-2"
          onMouseEnter={openNow}
          onMouseLeave={scheduleClose}
          onOpenAutoFocus={(event) => {
            // Keep focus on the trigger when the popover opens via hover —
            // tabbing into the content is still possible. Stops the page
            // from jumping when the user only mouses over.
            event.preventDefault();
          }}
        >
          <p className="text-sidebar-foreground/60 px-2 pb-1 text-[10px] font-medium tracking-wider uppercase">
            {group.title}
          </p>
          <ul className="flex flex-col gap-0.5">
            {visible.map((entry) => {
              if (isSubheader(entry)) {
                return (
                  <React.Fragment key={`sh-${entry.label}`}>
                    <PopoverSubheaderRow label={entry.label} />
                    {entry.items.map((sub) => (
                      <PopoverItem
                        key={sub.title}
                        item={sub}
                        hubUrl={hubUrl}
                        currentPath={currentPath}
                      />
                    ))}
                  </React.Fragment>
                );
              }
              return (
                <PopoverItem
                  key={entry.title}
                  item={entry}
                  hubUrl={hubUrl}
                  currentPath={currentPath}
                />
              );
            })}
          </ul>
        </PopoverContent>
      </Popover>
    );
  }

  const flatItems = visible.flatMap((entry) => (isSubheader(entry) ? entry.items : [entry]));
  const hasActiveChild = flatItems.some(
    (item) => item.to && (currentPath === item.to || currentPath.startsWith(`${item.to}/`)),
  );

  return (
    <Collapsible
      defaultOpen={hasActiveChild || (group.defaultExpanded ?? false)}
      className="group/collapsible"
    >
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton>
            <HugeiconsIcon icon={group.icon} strokeWidth={2} />
            <span>{group.title}</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90"
            >
              <path d="m9 18 6-6-6-6" />
            </svg>
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            {visible.map((entry) => {
              if (isSubheader(entry)) {
                return (
                  <React.Fragment key={`sh-${entry.label}`}>
                    <ExpandedSubheaderRow label={entry.label} />
                    {entry.items.map((sub) => (
                      <NavLink
                        key={sub.title}
                        item={sub}
                        hubUrl={hubUrl}
                        currentPath={currentPath}
                        isSub
                      />
                    ))}
                  </React.Fragment>
                );
              }
              return (
                <NavLink
                  key={entry.title}
                  item={entry}
                  hubUrl={hubUrl}
                  currentPath={currentPath}
                  isSub
                />
              );
            })}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}

export function QualitySidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const hubUrl = React.useMemo(() => getHubUrl(), []);
  const { state } = useSidebar();
  const partnerName = usePartnerName();
  const navItems = useNavItems();
  const currentPath = useRouterState({
    select: (state) => state.location.pathname,
  });

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              asChild
              tooltip="Quality"
              className="h-auto justify-center py-3"
            >
              <Link to="/">
                {state === 'collapsed' ? (
                  <LogoMark size="sm" />
                ) : (
                  <div className="flex w-full min-w-0 flex-col items-center gap-2">
                    <Logo className="h-8! w-auto!" />
                    <Badge
                      variant="secondary"
                      className="border-border text-muted-foreground max-w-full px-3"
                    >
                      <span className="truncate">{partnerName ?? 'Artic'}</span>
                    </Badge>
                  </div>
                )}
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>
          <SidebarMenu>
            {navItems.map((group) => {
              if (group.hide) return null;
              return (
                <NavGroupRender
                  key={group.title}
                  group={group}
                  hubUrl={hubUrl}
                  currentPath={currentPath}
                />
              );
            })}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <NavUser />
        <p className="text-muted-foreground text-center text-[10px] select-all">{__COMMIT_SHA__}</p>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
